import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, useDisclosure } from '@heroui/react';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import {
  useGetCourseByIdQuery,
  useGetTermsQuery,
  useDeleteGroupMutation,
  useDeleteTermMutation,
  Term,
} from '@/entities/Courses';
import { Container } from '@/shared/ui/Container';
import { CourseBreadcrumbs } from '@/shared/ui/CourseBreadcrumbs';
import { SectionLoader } from '@/shared/ui/Loader';
import { ContainerTable } from '@/shared/ui/AppTable';
import { MessageModal } from '@/shared/ui/Modal';
import { routerPath } from '@/shared/config/router';
import { Svg } from '@/shared/ui/Svg';
import { useToast } from '@/shared/lib/hooks/useToast';
import { useAdminAccess } from '@/shared/lib/adminAccess';
import EditIcon from '@/shared/assets/icons/edit.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import { AddTermModal } from '../components/AddTermModal';
import { EditTermModal } from '../components/EditTermModal';
import { EditGroupModal } from '../components/EditGroupModal';
import { DistributeStudentsModal } from '../components/DistributeStudentsModal';
import { termsColumns } from '../../config/termsColumns';
import styles from './GroupView.module.scss';

interface GroupViewProps {
  courseId: number;
  intakeId: number;
  groupNumber: number;
}

export const GroupView = ({ courseId, intakeId, groupNumber }: GroupViewProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canEntity } = useAdminAccess();
  // Распределение студентов по группе бэкенд проверяет как group.update
  const canGroupUpdate = canEntity('group', 'update');
  const canGroupDelete = canEntity('group', 'delete');
  const canTermCreate = canEntity('term', 'create');
  const canTermUpdate = canEntity('term', 'update');
  const canTermDelete = canEntity('term', 'delete');
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isEditGroupOpen,
    onOpen: onEditGroupOpen,
    onOpenChange: onEditGroupOpenChange,
  } = useDisclosure();
  const {
    isOpen: isEditTermOpen,
    onOpen: onEditTermOpen,
    onOpenChange: onEditTermOpenChange,
  } = useDisclosure();
  const {
    isOpen: isDistributeOpen,
    onOpen: onDistributeOpen,
    onOpenChange: onDistributeOpenChange,
  } = useDisclosure();
  const {
    isOpen: isDeleteGroupModalOpen,
    onOpen: onDeleteGroupModalOpen,
    onOpenChange: onDeleteGroupModalOpenChange,
  } = useDisclosure();
  const {
    isOpen: isDeleteTermModalOpen,
    onOpen: onDeleteTermModalOpen,
    onOpenChange: onDeleteTermModalOpenChange,
  } = useDisclosure();
  const [editingTerm, setEditingTerm] = useState<Term | null>(null);
  const [termToDelete, setTermToDelete] = useState<Term | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const { data, isLoading, isError, refetch } = useGetTermsQuery({ intakeId });
  const { data: courseData } = useGetCourseByIdQuery(courseId, { skip: !courseId });
  const [deleteGroup, { isLoading: isDeletingGroup }] = useDeleteGroupMutation();
  const [deleteTerm, { isLoading: isDeletingTerm }] = useDeleteTermMutation();

  const terms = useMemo(() => data?.data?.terms || [], [data?.data?.terms]);
  // Terms with dates in this intake: they are the neighbours that bound the edited term
  const scheduledTermNumbers = useMemo(
    () => terms.filter((term) => term.intakeTermId).map((term) => term.number),
    [terms],
  );
  // A term number can't be changed to the number of another term of the course
  const otherTermNumbers = useMemo(
    () => terms.filter((term) => term.id !== editingTerm?.id).map((term) => term.number),
    [terms, editingTerm],
  );
  const termsCount = terms.length;
  const courseTitle = courseData?.data?.course?.title || '';
  const intakeTitle = data?.data?.intake?.title || '';
  const intakeGroupLabel = intakeTitle ? `${intakeTitle} - Group ${groupNumber}` : `Group ${groupNumber}`;

  const handleSelectedItems = () => {
    // TODO: Handle selected terms if needed
  };

  const handleAddNewTerm = () => {
    onOpen();
  };

  const handleTermCreated = () => {
    refetch();
  };

  const handleDistributeStudents = () => {
    onDistributeOpen();
  };

  const handleStudentsAssigned = () => {
    refetch();
  };

  const handleTermClick = (term: Term) => {
    if (!term.intakeTermId || !term.startDate || !term.endDate) {
      toast({
        title: 'Date is not set',
        description: 'Please set the term start and end dates first.',
        color: 'warning',
      });
      return;
    }

    navigate(
      routerPath.termDetail
        .replace(':courseId', courseId.toString())
        .replace(':intakeId', intakeId.toString())
        .replace(':groupNumber', groupNumber.toString())
        .replace(':termId', term.id.toString()),
    );
  };

  const handleEditTerm = (term: Term) => {
    setEditingTerm(term);
    onEditTermOpen();
  };

  const handleEditTermSuccess = () => {
    setEditingTerm(null);
    refetch();
  };

  const handleDeleteTermClick = (term: Term) => {
    setTermToDelete(term);
    onDeleteTermModalOpen();
  };

  const handleDeleteTerm = async () => {
    if (!termToDelete) return;
    try {
      await deleteTerm(termToDelete.id).unwrap();
      toast({
        title: 'Success',
        description: 'Term deleted successfully',
        color: 'success',
      });
      setTermToDelete(null);
      onDeleteTermModalOpenChange();
      refetch();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to delete term',
        color: 'danger',
      });
      onDeleteTermModalOpenChange();
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await deleteGroup({ intakeId, groupNumber }).unwrap();
      toast({
        title: 'Success',
        description: 'Group deleted successfully',
        color: 'success',
      });
      onDeleteGroupModalOpenChange();
      navigate(routerPath.courseDetail.replace(':id', courseId.toString()));
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to delete group',
        color: 'danger',
      });
      onDeleteGroupModalOpenChange();
    }
  };

  const breadcrumbItems = [
    { label: 'Courses', href: routerPath.courses },
    { label: courseTitle || 'Course', href: routerPath.courseDetail.replace(':id', String(courseId)) },
    { label: intakeGroupLabel },
  ];

  return (
    <div className={styles.root}>
      <Container className="flex flex-col gap-4">
        <CourseBreadcrumbs items={breadcrumbItems} />

        <div className="flex items-end justify-between">
          <div className="inline-flex gap-x-2 items-end">
            <h2 className="text-[32px] font-medium">Group {groupNumber}</h2>
            <p className="text-grey-secondary leading-[220%]">(found {termsCount} terms)</p>
          </div>
          <div className="flex items-center gap-4">
            {canGroupUpdate && (
              <Button
                isIconOnly
                variant="light"
                onPress={onEditGroupOpen}
                className="text-grey-secondary hover:text-black"
              >
                <Svg Icon={EditIcon} width={20} height={20} />
              </Button>
            )}
            {canGroupDelete && (
              <Button
                isIconOnly
                variant="light"
                onPress={onDeleteGroupModalOpen}
                className="text-grey-secondary hover:text-black"
              >
                <Svg Icon={TrashIcon} width={20} height={20} className="text-icon-dizlike" />
              </Button>
            )}
            {canTermCreate && (
              <Button className="bg-[#211D55] text-white" onPress={handleAddNewTerm}>
                Add new term
              </Button>
            )}
            {canGroupUpdate && (
              <Button
                variant="bordered"
                className="border-[#211D55] bg-white text-[#211D55]"
                onPress={handleDistributeStudents}
              >
                Distribute the students
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {isLoading ? (
            <SectionLoader />
          ) : isError ? (
            <div className="bg-white p-6 rounded-[24px]">
              <div className="text-red-tertiary text-center py-8">
                Error loading terms. Please try again later.
              </div>
            </div>
          ) : (
            <ContainerTable<Term>
              data={terms}
              columns={termsColumns({
                onRowClick: handleTermClick,
                onEdit: canTermUpdate ? handleEditTerm : undefined,
                onDelete: canTermDelete ? handleDeleteTermClick : undefined,
              })}
              pagination={pagination}
              setPagination={setPagination as OnChangeFn<PaginationState>}
              hideTopActions={true}
              onSelectedItemsChange={handleSelectedItems}
              onRowClick={handleTermClick}
            />
          )}
        </div>
      </Container>
      <AddTermModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        intakeId={intakeId}
        freeRanges={data?.data?.freeRanges}
        takenNumbers={scheduledTermNumbers}
        onSuccess={handleTermCreated}
      />
      {editingTerm && (
        <EditTermModal
          isOpen={isEditTermOpen}
          onOpenChange={onEditTermOpenChange}
          intakeId={intakeId}
          term={editingTerm}
          termNumbers={scheduledTermNumbers}
          freeRanges={data?.data?.freeRanges}
          takenNumbers={otherTermNumbers}
          onSuccess={handleEditTermSuccess}
        />
      )}
      <EditGroupModal
        isOpen={isEditGroupOpen}
        onOpenChange={onEditGroupOpenChange}
        courseId={courseId}
        intakeId={intakeId}
        groupNumber={groupNumber}
        onSuccess={() => {
          refetch();
        }}
      />
      <DistributeStudentsModal
        isOpen={isDistributeOpen}
        onOpenChange={onDistributeOpenChange}
        intakeId={intakeId}
        groupNumber={groupNumber}
        onSuccess={handleStudentsAssigned}
      />
      <MessageModal
        title="Delete Group"
        text={`Are you sure you want to delete Group ${groupNumber}? This action cannot be undone. All related data (group lessons, group exam resubmissions, etc.) will be permanently deleted. Allowed only when the group has no enrollments and no assignment submissions.`}
        confirmText="Delete"
        isOpen={isDeleteGroupModalOpen}
        onOpenChange={onDeleteGroupModalOpenChange}
        variant="red"
        toggleVisibility={handleDeleteGroup}
        isLoading={isDeletingGroup}
      />
      <MessageModal
        title="Delete Term"
        text={
          termToDelete
            ? `Are you sure you want to delete Term ${termToDelete.number}? This action cannot be undone. All related data (modules, lessons, intake terms, intake modules, etc.) will be permanently deleted. Allowed only when there are no enrollments linked to this term (startTerm or currentTerm).`
            : ''
        }
        confirmText="Delete"
        isOpen={isDeleteTermModalOpen}
        onOpenChange={() => {
          setTermToDelete(null);
          onDeleteTermModalOpenChange();
        }}
        variant="red"
        toggleVisibility={handleDeleteTerm}
        isLoading={isDeletingTerm}
      />
    </div>
  );
};

